import { useTheme } from "@/contexts/ThemeContext";

const PUBLIC = process.env.PUBLIC_URL || "";

/**
 * Single, modular source of truth for the OFFICIAL horizontal ABAY360 logos.
 * Swap these paths to replace the logos platform-wide — no consumer/header
 * component needs to change.
 *
 *   light  → optimized for LIGHT backgrounds (navy wordmark)
 *   dark   → optimized for DARK backgrounds  (white wordmark)
 */
export const LOGO_SOURCES = {
  light: {
    webp: `${PUBLIC}/brand/abay360-horizontal-light.webp`,
    png: `${PUBLIC}/brand/abay360-horizontal-light.png`,
  },
  dark: {
    webp: `${PUBLIC}/brand/abay360-horizontal-dark.webp`,
    png: `${PUBLIC}/brand/abay360-horizontal-dark.png`,
  },
};

/**
 * Theme-aware ABAY360 horizontal logo.
 *
 * - variant="auto" (default): follows the ACTIVE resolved theme (System mode is
 *   already resolved to light/dark by ThemeContext, including live OS changes),
 *   so the correct logo swaps instantly with no page refresh.
 * - variant="light": force the light-background logo (navy wordmark).
 * - variant="dark": force the dark-background logo (white wordmark) — use on
 *   permanently dark surfaces (e.g. navy auth panel, footer badge, transparent
 *   hero header).
 *
 * Rendering uses <picture> (WebP + PNG fallback). Sizing is controlled purely
 * via `className`; the intrinsic aspect ratio is always preserved — the logo is
 * never stretched, cropped, or distorted. Both assets are preloaded in
 * index.html so switching is seamless with no layout shift.
 */
export const Logo = ({
  variant = "auto",
  className = "h-8 w-auto",
  alt = "ABAY360 — Find. Buy. Rent. Invest.",
  ...rest
}) => {
  const { theme } = useTheme();
  const key = variant === "auto" ? (theme === "dark" ? "dark" : "light") : variant;
  const src = LOGO_SOURCES[key] || LOGO_SOURCES.light;
  return (
    <picture>
      <source srcSet={src.webp} type="image/webp" />
      <img
        src={src.png}
        alt={alt}
        draggable={false}
        decoding="async"
        className={`block select-none ${className}`}
        {...rest}
      />
    </picture>
  );
};

export default Logo;
